import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { fetchQuestions, submitAssessment } from "@/lib/api";

const ScaleOption = ({ value, label, selected, onClick, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`min-h-[64px] sm:min-h-[72px] rounded-lg border text-left p-2 sm:p-3 transition-all ${
      selected
        ? "bg-blue-600 border-blue-600 text-white shadow-sm"
        : "bg-white border-slate-200 text-slate-700 hover:border-blue-300 hover:bg-blue-50"
    }`}
  >
    <div className={`font-display text-base sm:text-lg font-semibold leading-none ${selected ? "text-white" : "text-slate-900"}`}>{value}</div>
    <div className={`text-[10px] sm:text-[11px] mt-1 leading-tight ${selected ? "text-blue-100" : "text-slate-500"}`}>{label}</div>
  </button>
);

const Assessment = () => {
  const navigate = useNavigate();
  const questionsQuery = useQuery({
    queryKey: ["full-questions"],
    queryFn: fetchQuestions,
    retry: false,
    staleTime: Infinity,
  });
  const data = questionsQuery.data;
  const [step, setStep] = useState(0); // 0..5 = category steps (2 q each), 6 = lead capture
  const [answers, setAnswers] = useState({});
  const [lead, setLead] = useState({
    name: "", email: "", organization: "", role: "", industry: "",
    company_size: "", organization_type: "", current_systems: "",
    biggest_challenge: "", long_term_objective: "", follow_up_interest: "yes",
  });
  const [submitting, setSubmitting] = useState(false);

  if (questionsQuery.isError) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-32 text-slate-500 gap-3">
          <p>Couldn&apos;t load the assessment. Please refresh and try again.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-32 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading assessment…
        </div>
      </div>
    );
  }

  const totalSteps = data.categories.length + 1; // 6 category steps + 1 lead
  const isLeadStep = step === data.categories.length;
  const currentCategory = isLeadStep ? null : data.categories[step];
  const questionsForStep = isLeadStep ? [] : data.questions.filter((q) => q.category === currentCategory);

  const allAnswered = isLeadStep || questionsForStep.every((q) => answers[q.id]);
  const totalAnswered = Object.keys(answers).length;
  const progressPct = Math.round(((step + (isLeadStep ? 1 : 0)) / totalSteps) * 100);

  const handleSelect = (qid, val) => setAnswers((a) => ({ ...a, [qid]: val }));

  const next = () => {
    if (!allAnswered) {
      toast.error("Please answer all questions on this step.");
      return;
    }
    if (step < totalSteps - 1) setStep(step + 1);
  };
  const prev = () => step > 0 && setStep(step - 1);

  const requiredLeadFields = ["name", "email", "organization", "role", "industry", "company_size", "organization_type"];
  const leadValid = requiredLeadFields.every((k) => lead[k]?.trim?.());

  const submit = async () => {
    if (!leadValid) {
      toast.error("Please complete all required fields.");
      return;
    }
    if (totalAnswered !== data.questions.length) {
      toast.error("Some assessment questions are unanswered.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await submitAssessment({ lead, answers });
      navigate(`/results/${result.id}`, { state: { result } });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-10 lg:py-16">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between items-baseline mb-2">
            <div className="text-xs font-semibold tracking-[0.18em] uppercase text-slate-500">
              {isLeadStep ? `Final Step · About You` : `Step ${step + 1} of ${totalSteps} · ${currentCategory}`}
            </div>
            <div className="text-xs font-mono text-slate-400">{progressPct}%</div>
          </div>
          <Progress value={progressPct} data-testid="assessment-progress" className="h-1.5 bg-slate-100" />
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            {!isLeadStep ? (
              <div className="space-y-8">
                <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">{currentCategory}</h1>
                {questionsForStep.map((q, idx) => (
                  <div key={q.id} className="bg-white border border-slate-200 rounded-xl p-6">
                    <div className="text-xs font-mono text-slate-400 mb-2">Q{idx + 1 + step * 2} / {data.questions.length}</div>
                    <p className="text-base font-medium text-slate-900 leading-relaxed mb-5">{q.text}</p>
                    <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
                      {data.answer_scale.map((opt) => (
                        <ScaleOption
                          key={opt.value}
                          testid={`${q.id}-option-${opt.value}`}
                          value={opt.value}
                          label={opt.label}
                          selected={answers[q.id] === opt.value}
                          onClick={() => handleSelect(q.id, opt.value)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <LeadCaptureForm lead={lead} setLead={setLead} />
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex justify-between mt-10">
          <Button
            variant="outline"
            onClick={prev}
            disabled={step === 0}
            data-testid="assessment-prev-btn"
            className="rounded-md border-slate-300"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          {isLeadStep ? (
            <Button
              onClick={submit}
              disabled={submitting || !leadValid}
              data-testid="assessment-submit-btn"
              className="bg-[#312D44] hover:bg-[#1F1B30] text-white rounded-md px-6"
            >
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating your results…</> : <>See my readiness results <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          ) : (
            <Button
              onClick={next}
              data-testid="assessment-next-btn"
              className="bg-[#312D44] hover:bg-[#1F1B30] text-white rounded-md px-6"
            >
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const LeadCaptureForm = ({ lead, setLead }) => {
  const set = (k) => (e) => setLead((l) => ({ ...l, [k]: e.target?.value ?? e }));
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">A few details, then your results.</h1>
        <p className="mt-3 text-slate-600">Eleven quick fields, then we&apos;ll show your scored readiness and a tailored narrative interpretation on the next screen.</p>
        <p className="mt-2 text-sm text-slate-500">
          By submitting, you agree to our <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">privacy practices</a>. We retain submissions for 24 months and never share them with advertisers.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Full name *" id="name"><Input data-testid="lead-name" value={lead.name} onChange={set("name")} placeholder="Jane Rivera" /></Field>
          <Field label="Work email *" id="email"><Input data-testid="lead-email" type="email" value={lead.email} onChange={set("email")} placeholder="jane@company.com" /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Organization *" id="organization"><Input data-testid="lead-org" value={lead.organization} onChange={set("organization")} placeholder="Acme Corp" /></Field>
          <Field label="Role / Title *" id="role"><Input data-testid="lead-role" value={lead.role} onChange={set("role")} placeholder="VP, Transformation" /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Industry *" id="industry">
            <SelectField testid="lead-industry" value={lead.industry} onChange={(v) => setLead((l) => ({ ...l, industry: v }))}
              options={["Financial Services", "Healthcare", "Manufacturing", "Energy & Utilities", "Government", "Technology", "Retail & Consumer", "Professional Services", "Education", "Other"]} placeholder="Select industry" />
          </Field>
          <Field label="Company size *" id="company_size">
            <SelectField testid="lead-size" value={lead.company_size} onChange={(v) => setLead((l) => ({ ...l, company_size: v }))}
              options={["1–50", "51–250", "251–1,000", "1,001–5,000", "5,001–20,000", "20,000+"]} placeholder="Select size" />
          </Field>
        </div>
        <Field label="Organization type *" id="organization_type">
          <SelectField testid="lead-orgtype" value={lead.organization_type} onChange={(v) => setLead((l) => ({ ...l, organization_type: v }))}
            options={["Public company", "Private company", "Non-profit", "Government / Public sector", "Educational institution"]} placeholder="Select type" />
        </Field>
        <Field label="Current systems & tools" id="current_systems"><Textarea data-testid="lead-systems" value={lead.current_systems} onChange={set("current_systems")} placeholder="e.g., Salesforce, SAP, ServiceNow, custom ERP…" rows={2} /></Field>
        <Field label="Your biggest transformation challenge" id="biggest_challenge"><Textarea data-testid="lead-challenge" value={lead.biggest_challenge} onChange={set("biggest_challenge")} placeholder="What's actually slowing things down right now?" rows={3} /></Field>
        <Field label="Long-term objective" id="long_term_objective"><Textarea data-testid="lead-objective" value={lead.long_term_objective} onChange={set("long_term_objective")} placeholder="What does success look like 12–24 months out?" rows={2} /></Field>
        <Field label="Open to a follow-up conversation?" id="follow_up_interest">
          <SelectField testid="lead-followup" value={lead.follow_up_interest} onChange={(v) => setLead((l) => ({ ...l, follow_up_interest: v }))}
            options={["yes", "maybe", "no"]} placeholder="Select" />
        </Field>
      </div>
    </div>
  );
};

const Field = ({ label, id, children }) => (
  <div>
    <Label htmlFor={id} className="text-sm font-medium text-slate-700 mb-1.5 block">{label}</Label>
    {children}
  </div>
);

const SelectField = ({ value, onChange, options, placeholder, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="bg-white border-slate-300"><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default Assessment;
